import React, { useState } from "react";
import * as XLSX from "xlsx";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import { useGetRentsQuery } from "../../slices/rentsApiSlice";
import { useGetPaymentsQuery } from "../../slices/paymentsApiSlice";

export default function BalanceByCustomer() {
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [summaryByDate, setSummaryByDate] = useState({});
  const [incomeBreakdownsByDate, setIncomeBreakdownsByDate] = useState({});

  const { data: salesData } = useGetSalesQuery();
  const { data: expensesData } = useGetExpensesQuery({ startDate, endDate });
  const { data: incomesData } = useGetIncomesQuery({ startDate, endDate });
  const { data: depositsData } = useGetDepositsQuery({ startDate, endDate });
  const { data: suppliesData } = useGetSuppliesQuery();
  const { data: rentsData } = useGetRentsQuery({ start: startDate, end: endDate });
  const { data: paymentsData } = useGetPaymentsQuery({ startDate, endDate });

  const getDateRange = (start, end) => {
    const dates = [];
    let current = new Date(start);
    const last = new Date(end);
    while (current <= last) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const filterByDate = (data, date) =>
    data?.filter(item => item.date?.startsWith(date)) || [];

  const groupByName = (items, key, getName) => {
    return items.reduce((acc, item) => {
      const name = getName(item) || "Unknown";
      acc[name] = (acc[name] || 0) + Number(item[key] || 0);
      return acc;
    }, {});
  };

  const groupIncomeByCategory = (items) => {
    const grouped = { Morning: 0, Afternoon: 0, Night: 0 };
    items.forEach(item => {
      const category = item.category;
      const amount = Number(item.amount || 0);
      if (grouped.hasOwnProperty(category)) {
        grouped[category] += amount;
      }
    });
    return grouped;
  };

  const calculateSummary = () => {
    const dates = getDateRange(startDate, endDate);
    const summary = {};
    const breakdowns = {};

    dates.forEach(date => {
      const sales = groupByName(
        filterByDate(salesData?.sales, date),
        "totalPrice",
        item => item.customerDetails?.name
      );

      const expenses = groupByName(
        filterByDate(expensesData?.expenses, date),
        "amount",
        item => item.accountName
      );

      const incomes = filterByDate(incomesData?.incomes, date);
      const incomeTotals = incomes.reduce((acc, item) => {
        const name = item.username || "Unknown";
        acc[name] = (acc[name] || 0) + Number(item.amount || 0);
        return acc;
      }, {});

      const deposits = groupByName(
        filterByDate(depositsData?.deposits, date),
        "amount",
        item => item.username
      );

      const supplies = groupByName(
        filterByDate(suppliesData?.supplies, date),
        "cardCost",
        item => item.username
      );

      const rents = groupByName(
        filterByDate(rentsData, date),
        "totalRent",
        item => item.tenant?.name
      );

      const payments = groupByName(
        filterByDate(paymentsData?.payments, date),
        "amount",
        item => item.landlordName
      );

      breakdowns[date] = groupIncomeByCategory(incomes);

      const allNames = new Set([
        ...Object.keys(sales),
        ...Object.keys(expenses),
        ...Object.keys(incomeTotals),
        ...Object.keys(deposits),
        ...Object.keys(supplies),
        ...Object.keys(rents),
        ...Object.keys(payments),
      ]);

      const rows = Array.from(allNames).map(name => ({
        name,
        sales: sales[name] || 0,
        expenses: expenses[name] || 0,
        incomes: incomeTotals[name] || 0,
        deposits: deposits[name] || 0,
        supplies: supplies[name] || 0,
        rent: rents[name] || 0,
        payments: payments[name] || 0,
      }));

      const totals = rows.reduce(
        (acc, row) => {
          acc.sales += row.sales;
          acc.expenses += row.expenses;
          acc.incomes += row.incomes;
          acc.deposits += row.deposits;
          acc.supplies += row.supplies;
          acc.rent += row.rent;
          acc.payments += row.payments;

          acc.balance +=
  Number(row.incomes || 0) -
  (
    Number(row.sales || 0) +
    Number(row.expenses || 0) +
    Number(row.rent || 0) +
    Number(row.payments || 0)
  );

          return acc;
        },
        {
          sales: 0,
          expenses: 0,
          incomes: 0,
          deposits: 0,
          supplies: 0,
          rent: 0,
          payments: 0,
          balance: 0,
        }
      );

      summary[date] = { rows, totals };
    });

    setSummaryByDate(summary);
    setIncomeBreakdownsByDate(breakdowns);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    Object.entries(summaryByDate).forEach(([date, { rows, totals }]) => {
      const sheetData = [
        ...rows.map(row => ({
          Date: date,
          Customer: row.name,
          Sales: Number(row.sales).toFixed(2),
          Expenses: Number(row.expenses).toFixed(2),
          Incomes: Number(row.incomes).toFixed(2),
          Deposits: Number(row.deposits).toFixed(2),
          Supplies: Number(row.supplies).toFixed(2),
          Rent: Number(row.rent).toFixed(2),
          Payments: Number(row.payments).toFixed(2),
          Balance: Number(row.balance).toFixed(2),
        })),
        {
          Date: date,
          Customer: "TOTAL",
          Sales: Number(totals.sales).toFixed(2),
          Expenses: Number(totals.expenses).toFixed(2),
          Incomes: Number(totals.incomes).toFixed(2),
          Deposits: Number(totals.deposits).toFixed(2),
          Supplies: Number(totals.supplies).toFixed(2),
          Rent: Number(totals.rent).toFixed(2),
          Payments: Number(totals.payments).toFixed(2),
          Balance: Number(totals.balance).toFixed(2),
        },
      ];

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, date);
    });

    XLSX.writeFile(workbook, `CustomerSummary_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="range-customer-summary">
      <h2>Summary by Customer and Date</h2>

      <div className="date-range">
        <label>
          Start Date:
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </label>
        <label>
          End Date:
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </label>
      </div>

      <div className="actions">
        <button onClick={calculateSummary}>Generate Summary</button>
        <button onClick={exportToExcel} disabled={Object.keys(summaryByDate).length === 0}>
          Export to Excel
        </button>
      </div>

      {Object.entries(summaryByDate).map(([date, { rows, totals }]) => (
        <div key={date} className="daily-summary">
          <h3>{date}</h3>

          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Sales</th>
                <th>Inc/Exp</th>
                <th>CashCollected</th>
                <th>Deposits</th>
                <th>Supplies</th>
                <th>Rent</th>
                <th>Payments</th>
                <th>Balance</th>
              </tr>
            </thead>

            <tbody>
              {rows.map(row => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  <td>{Number(row.sales).toFixed(2)}</td>
                  <td>{Number(row.expenses).toFixed(2)}</td>
                  <td>{Number(row.incomes).toFixed(2)}</td>
                  <td>{Number(row.deposits).toFixed(2)}</td>
                  <td>{Number(row.supplies).toFixed(2)}</td>
                  <td>{Number(row.rent).toFixed(2)}</td>
                  <td>{Number(row.payments).toFixed(2)}</td>
                  <td>
                  {(
                    Number(row.incomes || 0) -
                    (
                      Number(row.sales || 0) +
                      Number(row.expenses || 0) +
                      Number(row.rent || 0) +
                      Number(row.payments || 0)
                    )
                  ).toFixed(2)}
                </td>
                  
                </tr>
              ))}

              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td>{Number(totals.sales).toFixed(2)}</td>
                <td>{Number(totals.expenses).toFixed(2)}</td>
                <td>{Number(totals.incomes).toFixed(2)}</td>
                <td>{Number(totals.deposits).toFixed(2)}</td>
                <td>{Number(totals.supplies).toFixed(2)}</td>
                <td>{Number(totals.rent).toFixed(2)}</td>
                <td>{Number(totals.payments).toFixed(2)}</td>
                <td>{Number(totals.balance).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <h4>Income Breakdown</h4>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(incomeBreakdownsByDate[date] || {}).map(([category, amount]) => (
                <tr key={category}>
                  <td>{category}</td>
                  <td>{Number(amount).toFixed(2)}</td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td>
                  {Number(
                    Object.values(incomeBreakdownsByDate[date] || {})
                      .reduce((sum, val) => sum + val, 0)
                  ).toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>

        </div>
      ))}
    </div>
  );
}